import React, { useReducer, useContext, useCallback } from "react";
const TestResultsContext = React.createContext(null);

const initialState = {
  // Stores which sample tests are having results added
  addingSampleTestIds: [],
  // Stores the results for the sample tests being edited
  results: {}
};

function resultReducer(state, action) {
  switch (action.type) {
    case "addResults":
      var sampleTestIds = action.sampleTestIds;
      return {
        ...state,
        addingSampleTestIds: [...state.addingSampleTestIds, ...sampleTestIds]
      };
    case "stopAddingResults":
      var sampleTestIds = action.sampleTestIds || [];
      var newResults = state.results;
      sampleTestIds.forEach(id => delete newResults[id]);
      return {
        results: newResults,
        addingSampleTestIds: state.addingSampleTestIds.filter(
          id => !sampleTestIds.includes(id)
        )
      };
    case "reset":
      return initialState;
    case "update":
      var sampleTestId = action.sampleTestId;
      var result = action.result;
      return {
        ...state,
        results: {
          ...state.results,
          [sampleTestId]: result
        }
      };
    case "setAllQualifiers":
      var qualifier = action.qualifier;
      var newState = { ...state };
      for (var sampleTestId in state.results) {
        newState.results[sampleTestId].qualifier = qualifier;
      }
      return newState;
    default:
      return state;
  }
}

function useTestResultsContext() {
  const { state, dispatch } = useContext(TestResultsContext);

  const reset = () => dispatch({ type: "reset" });

  const updateResult = useCallback(
    ({ sampleTestId, result }) => dispatch({ type: "update", sampleTestId, result }),
    [dispatch]
  );

  const isAddingResult = useCallback(
    sampleTestId => state.addingSampleTestIds.includes(sampleTestId),
    [dispatch, state.addingSampleTestIds]
  );

  const startAddingResults = useCallback(
    sampleTestIds =>
      dispatch({
        type: "addResults",
        sampleTestIds: sampleTestIds || []
      }),
    [dispatch]
  );

  const stopAddingResults = useCallback(
    sampleTestIds =>
      dispatch({
        type: "stopAddingResults",
        sampleTestIds: sampleTestIds || []
      }),
    [dispatch]
  );

  const setAllQualifiers = useCallback(
    qualifier =>
      dispatch({
        type: "setAllQualifiers",
        qualifier
      }),
    [dispatch]
  );

  return {
    addingResults: state.addingSampleTestIds.length > 0,
    state,
    dispatch,
    reset,
    updateResult,
    isAddingResult,
    startAddingResults,
    stopAddingResults,
    setAllQualifiers
  };
}

const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(resultReducer, undefined, () => initialState);
  const value = { state, dispatch };
  return <TestResultsContext.Provider value={value}>{children}</TestResultsContext.Provider>;
};

export default TestResultsContext;
export { resultReducer, Provider, useTestResultsContext };
